import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import {
  RentCollectionService,
  PaymentMethodOption,
  PayoutAccount,
  ReminderSettings,
  ReminderRule,
} from 'src/shared/services/rent-collection.service';

@Component({
  templateUrl: 'rent-collection-settings.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class RentCollectionSettingsPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private storage = inject(TidyStorage);
  private rightSidePanelService = inject(RightSidePanelService);
  private fb = inject(FormBuilder);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private rentService = inject(RentCollectionService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  loaded = false;
  errorMessage: string;

  methods: PaymentMethodOption[] = [];
  methodHeaders: string[] = ['Method', 'Rate', 'Fee Paid By', 'Accepted', 'last-column'];
  methodRows: any[] = [];
  feePayerSelectItems = [
    { viewValue: 'Tenant pays', value: 'tenant' },
    { viewValue: 'Owner absorbs', value: 'owner' },
  ];
  acceptedSelectItems = [
    { viewValue: 'Enabled', value: 'enabled' },
    { viewValue: 'Disabled', value: 'disabled' },
  ];

  payout: PayoutAccount;
  isConnecting = false;
  scheduleItems = [
    { viewValue: 'Daily', value: 'Daily' },
    { viewValue: 'Weekly', value: 'Weekly' },
    { viewValue: 'Monthly', value: 'Monthly' },
  ];

  reminders: ReminderSettings;
  reminderForm: UntypedFormGroup;
  rulesForm: UntypedFormGroup;
  accentColor = '#1a8e9c';
  accentSwatches = ['#1a8e9c', '#2E7D32', '#3F51B5', '#EF6C00', '#C2185B', '#1E2125'];

  connectPayoutAction = () => this.connectPayout();
  saveRemindersAction = () => this.saveReminders();

  constructor() {
    this.reminderForm = this.fb.group({ heading: [''], message: [''] });
    this.rulesForm = this.fb.group({});
    this.reminderForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    if (this.isRightSideContent) {
      this.rightSidePanelService.setDialogPageTitle('Rent Collection Settings');
    }
    try {
      await this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load settings';
      this.util.showError(this.errorMessage, 10000);
    }
  }

  async loadPage() {
    [this.methods, this.payout, this.reminders] = await Promise.all([
      this.rentService.getPaymentMethods(),
      this.rentService.getPayoutAccount(),
      this.rentService.getReminderSettings(),
    ]);
    this.methodRows = this.buildMethodRows();
    this.accentColor = this.reminders.accent_color;
    this.reminderForm.patchValue(
      { heading: this.reminders.heading, message: this.reminders.message },
      { emitEvent: false }
    );
    this.buildRulesForm();
    this.loaded = true;
    this.cdr.markForCheck();
  }

  // One control per reminder rule; edits autosave (debounced) so there's no
  // separate save step for the message text.
  buildRulesForm(): void {
    this.reminders.rules.forEach(rule => {
      const control = this.fb.control(rule.message);
      this.rulesForm.addControl(`rule_${  rule.id}`, control);
      control.valueChanges.pipe(
        debounceTime(400),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(value => {
        rule.message = value;
        this.rentService.updateReminderRuleMessage(rule.id, value);
      });
    });
  }

  // ---- Payment methods ----
  buildMethodRows(): any[] {
    return this.methods.map(m => ({
      id: m.key,
      'Method': { value: m.label },
      'Rate': { value: m.rate },
      'Fee Paid By': { value: m.fee_paid_by, selectItems: this.feePayerSelectItems },
      'Accepted': { value: m.enabled ? 'enabled' : 'disabled', selectItems: this.acceptedSelectItems },
      'last-column': { value: '' },
    }));
  }

  async onMethodSelect(event: { value: string; column: string; element: any }): Promise<void> {
    if (this.featureGate.gate()) {
      // Repaint the table so the select snaps back to the persisted value.
      this.methodRows = this.buildMethodRows();
      this.cdr.markForCheck();
      return;
    }
    const method = this.methods.find(m => m.key === event.element.id);
    if (!method) { return; }
    if (event.column === 'Fee Paid By') {
      method.fee_paid_by = event.value as 'tenant' | 'owner';
      await this.rentService.setPaymentMethodFeePayer(method.key, method.fee_paid_by);
    } else if (event.column === 'Accepted') {
      method.enabled = event.value === 'enabled';
      await this.rentService.setPaymentMethodEnabled(method.key, method.enabled);
    }
    this.cdr.markForCheck();
  }

  // ---- Payout ----
  async connectPayout(): Promise<void> {
    if (this.featureGate.gate()) { return; }
    this.isConnecting = true;
    this.cdr.markForCheck();
    try {
      this.payout = await this.rentService.connectPayout();
      this.util.showSuccess(`${this.payout.provider} payout account connected. Collected funds will deposit here.`);
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message;
    } finally {
      this.isConnecting = false;
      this.cdr.markForCheck();
    }
  }

  async setSchedule(schedule: any): Promise<void> {
    this.featureGate.gate();
  }

  // ---- Reminders ----
  get previewHeading(): string {
    return this.reminderForm.get('heading')?.value || this.reminders?.heading || '';
  }

  get previewMessage(): string {
    return this.reminderForm.get('message')?.value || this.reminders?.message || '';
  }

  async toggleRule(rule: ReminderRule): Promise<void> {
    this.featureGate.gate();
  }

  selectAccent(color: string): void {
    this.accentColor = color;
    this.cdr.markForCheck();
  }

  uploadLogo(): void {
    this.featureGate.gate();
  }

  async saveReminders(): Promise<void> {
    this.featureGate.gate();
  }
}
